import {
  Button,
  ButtonVariant,
  IconButton,
  IconIdentifier,
  type SelectOption,
  Tooltip,
} from '@aus-platform/design-system';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import classNames from 'classnames';
import { isEmpty, isNil } from 'lodash';
import {
  selectMap3dDataset,
  selectMap3DState,
  selectMap3dWorkspace,
  setAIActiveOrthoLayerId,
  setAIMode,
  setAIPromptType,
  setShowUnsavedFeaturesModal,
  setWorkspaceInstruction,
} from 'map-3d/shared/map-3d-slices';
import { useDrawingInProgress } from 'map-3d/shared/map-3d-tools/drawing-tools';
import { WorkspaceTools } from 'map-3d/shared/map-3d-tools/enums';
import React, { useEffect, useMemo, useState } from 'react';
import { LayerType } from 'shared/api';
import { PromptType } from 'shared/hooks/polygon-ai';
import {
  isCursorWithinLayerBounds,
  type WorkspaceLayer,
  zoomToLayer,
} from 'src/map-3d/components/map-3d-sidebar';
import { PolygonEventType } from 'src/map-3d/shared';
import { BetaPill } from 'src/shared/components';
import { DRAWING_INSTRUCTION } from '../constants';
import { UnsavedChangesActionType } from '../enums';
import { PolygonAIModeToggle } from './components/polygon-ai-mode-toggle';
import { PolygonDatasetDropdown } from './components/polygon-dataset-dropdown';
import { POLYGON_AI_TOOL_INSTRUCTION } from './constants';
import { type PolygonSecondaryToolboxProps } from './types';

export const PolygonSecondaryToolbox: React.FC<
  PolygonSecondaryToolboxProps
> = ({ polygonAISession, setPendingAction }) => {
  // States.
  const [canUndo, setCanUndo] = useState(false);
  const [hasCreatedPolygonInSession, setHasCreatedPolygonInSession] =
    useState(false);

  // Selectors.
  const {
    workspaceLayers,
    polygonAiConfig: polygonAi,
    actionTools,
    drawingTools,
  } = useAppSelector(selectMap3dWorkspace);
  const { activeWorkspaceSite } = useAppSelector(selectMap3dDataset);
  const { cesiumProxy } = useAppSelector(selectMap3DState);

  // Hooks.
  const { undo, getConfirmedPolygons, hasPrompts } = polygonAISession;
  const isDrawingInProgress = useDrawingInProgress();

  // Dispatch.
  const dispatch = useAppDispatch();

  // Memos.
  const datasetOptions = useMemo<SelectOption<WorkspaceLayer>[]>(() => {
    return Object.values(workspaceLayers)
      .filter((layer) => layer.type === LayerType.Orthomosaic && layer.show)
      .sort((a, b) => (b.zIndex ?? 0) - (a.zIndex ?? 0))
      .map((layer) => ({
        label: layer.name,
        value: layer,
      }));
  }, [workspaceLayers]);

  // Variables.
  const { isAiModeEnabled, activeOrthoLayerId, promptType } = polygonAi;
  const hasDatasetLayers = !isEmpty(datasetOptions);
  const hasUnsavedAIPolygons = !isEmpty(getConfirmedPolygons());
  const isUndoDisabled = isAiModeEnabled
    ? !hasUnsavedAIPolygons && !hasPrompts
    : !canUndo;

  const hasSelectedPrompt = polygonAISession.hasSelectedPrompt?.() ?? false;
  const polygonDrawingInstruction =
    DRAWING_INSTRUCTION[WorkspaceTools.PolygonTool] ?? null;

  // Effects.
  useEffect(() => {
    return () => {
      dispatch(setAIMode(false));
      dispatch(setWorkspaceInstruction(null));
    };
  }, []);

  useEffect(() => {
    let instruction: string | null;

    if (!hasDatasetLayers || (isAiModeEnabled && isNil(activeOrthoLayerId))) {
      instruction = POLYGON_AI_TOOL_INSTRUCTION.noOrthomosaic;
    } else if (!isAiModeEnabled) {
      if (isDrawingInProgress) {
        instruction = polygonDrawingInstruction;
      } else {
        instruction = hasCreatedPolygonInSession
          ? null
          : POLYGON_AI_TOOL_INSTRUCTION.enableMode;
      }
    } else if (promptType === PromptType.Negative) {
      instruction = POLYGON_AI_TOOL_INSTRUCTION.refineMode;
    } else {
      instruction = hasSelectedPrompt
        ? POLYGON_AI_TOOL_INSTRUCTION.keepClicking
        : POLYGON_AI_TOOL_INSTRUCTION.addPolygons;
    }

    dispatch(setWorkspaceInstruction(instruction));
  }, [
    hasDatasetLayers,
    isAiModeEnabled,
    hasCreatedPolygonInSession,
    activeOrthoLayerId,
    promptType,
    hasSelectedPrompt,
    isDrawingInProgress,
  ]);

  useEffect(() => {
    const polygonTool = drawingTools?.polygonTool;

    if (isNil(polygonTool)) {
      return;
    }

    const handlePolygonCreated = () => {
      if (!isAiModeEnabled) {
        setHasCreatedPolygonInSession(true);
      }
    };

    polygonTool.addEventListener(
      PolygonEventType.PolygonCreated,
      handlePolygonCreated,
    );

    return () => {
      polygonTool.removeEventListener(
        PolygonEventType.PolygonCreated,
        handlePolygonCreated,
      );
    };
  }, [drawingTools, isAiModeEnabled]);

  useEffect(() => {
    if (!hasDatasetLayers && isAiModeEnabled) {
      dispatch(setAIMode(false));
    }
  }, [isAiModeEnabled, hasDatasetLayers]);

  useEffect(() => {
    const polygonTool = drawingTools?.polygonTool;

    if (isNil(polygonTool)) {
      return;
    }

    const updateUndoState = () => {
      setCanUndo(polygonTool.polygonTool.polygonDrawing.canPerformUndo());
    };

    polygonTool.addEventListener(
      PolygonEventType.DrawingStarted,
      updateUndoState,
    );

    polygonTool.addEventListener(PolygonEventType.DrawingEnd, updateUndoState);

    polygonTool.addEventListener(
      PolygonEventType.VertexModifiedInPolygon,
      updateUndoState,
    );

    return () => {
      polygonTool.removeEventListener(
        PolygonEventType.DrawingStarted,
        updateUndoState,
      );

      polygonTool.removeEventListener(
        PolygonEventType.DrawingEnd,
        updateUndoState,
      );

      polygonTool.removeEventListener(
        PolygonEventType.VertexModifiedInPolygon,
        updateUndoState,
      );
    };
  }, [drawingTools]);

  // Handlers.
  const enableAIMode = () => {
    if (!hasDatasetLayers || hasUnsavedAIPolygons) {
      return;
    }

    drawingTools?.deactivateAllTools();
    actionTools?.deactivateAllTools();

    dispatch(setAIPromptType(PromptType.Positive));
    dispatch(setAIMode(true));

    if (isNil(activeOrthoLayerId)) {
      const layer = datasetOptions[0]?.value;

      dispatch(setAIActiveOrthoLayerId(layer.id));

      if (!isCursorWithinLayerBounds(layer, cesiumProxy?.lastMouseLocation)) {
        zoomToLayer(layer, cesiumProxy, activeWorkspaceSite);
      }
    }
  };

  const disableAIMode = () => {
    if (hasUnsavedAIPolygons) {
      setPendingAction({
        type: UnsavedChangesActionType.DisableAIMode,
      });
      dispatch(setShowUnsavedFeaturesModal(true));
      return;
    }

    dispatch(setAIMode(false));
    drawingTools?.polygonTool?.activate();
  };

  const onClickPositive = () => {
    dispatch(setAIPromptType(PromptType.Positive));
  };

  const onClickNegative = () => {
    dispatch(setAIPromptType(PromptType.Negative));
  };

  const onClickUndo = () => {
    if (isAiModeEnabled) {
      undo();
      return;
    }

    drawingTools?.polygonTool.undo();
  };

  const handleOrthoChange = (newLayer: WorkspaceLayer) => {
    if (hasUnsavedAIPolygons) {
      setPendingAction({
        type: UnsavedChangesActionType.ChangeOrthomosaic,
        layer: newLayer,
      });

      dispatch(setShowUnsavedFeaturesModal(true));
      return;
    }

    dispatch(setAIActiveOrthoLayerId(newLayer.id));

    if (!isCursorWithinLayerBounds(newLayer, cesiumProxy?.lastMouseLocation)) {
      zoomToLayer(newLayer, cesiumProxy, activeWorkspaceSite);
    }
  };

  // Classnames.
  const toolbarClassnames = classNames('polygon-secondary-toolbox', {
    on: isAiModeEnabled,
  });

  // Renderers.
  const aiToggleButtonTooltipWrapper = (aiModeToggle: React.ReactNode) => {
    if (hasDatasetLayers) {
      return aiModeToggle;
    }

    return (
      <Tooltip
        className="polygon-secondary-toolbox__ai-mode-disabled-tooltip"
        hoverText={
          <span className="polygon-secondary-toolbox__ai-mode-disabled-tooltip__copy">
            To use AI-assisted digitisation, ensure an orthomosaic is loaded and
            visible in the workspace.
          </span>
        }
      >
        {aiModeToggle}
      </Tooltip>
    );
  };

  return (
    <div className={toolbarClassnames}>
      <BetaPill />
      {aiToggleButtonTooltipWrapper(
        <PolygonAIModeToggle
          isAiModeEnabled={isAiModeEnabled}
          disabled={!hasDatasetLayers}
          onToggle={isAiModeEnabled ? disableAIMode : enableAIMode}
        />,
      )}

      {isAiModeEnabled && (
        <>
          <div className="polygon-secondary-toolbox__separator" />
          <PolygonDatasetDropdown
            options={datasetOptions}
            selectedId={activeOrthoLayerId}
            handleOrthoChange={handleOrthoChange}
          />

          <div className="polygon-secondary-toolbox__ai-prompt">
            <Tooltip
              className="polygon-secondary-toolbox__ai-prompt__tooltip"
              hoverText="Add Polygons"
            >
              <IconButton
                className="polygon-secondary-toolbox__ai-prompt__icon-btn positive"
                iconIdentifier={IconIdentifier.Plus}
                onClick={onClickPositive}
                isActive={promptType === PromptType.Positive}
                iconSize={20}
              />
            </Tooltip>
            <Tooltip
              className="polygon-secondary-toolbox__ai-prompt__tooltip"
              hoverText="Refine Polygons"
            >
              <IconButton
                className="polygon-secondary-toolbox__ai-prompt__icon-btn negative"
                iconIdentifier={IconIdentifier.Minus}
                onClick={onClickNegative}
                isActive={promptType === PromptType.Negative}
                iconSize={20}
              />
            </Tooltip>
          </div>
        </>
      )}

      <div className="polygon-secondary-toolbox__separator" />

      <Button
        className="polygon-secondary-toolbox__action-btn"
        rightIconIdentifier={IconIdentifier.Undo}
        onClick={onClickUndo}
        variant={ButtonVariant.Link}
        disabled={isUndoDisabled}
      >
        Undo
      </Button>
    </div>
  );
};
